/**
 * API统一配置管理
 * 所有API相关的配置都在这里统一管理
 * 修改服务地址时，只需修改此文件即可
 */

/**
 * 获取API基础URL
 * 优先使用环境变量，如果没有则使用默认值
 */
export const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
};

/**
 * API基础配置
 */
export const API_CONFIG = {
  // 后端服务地址 - 不要在这里添加/api，在controller路径中已包含
  BASE_URL: getApiBaseUrl(),
  
  // WebSocket地址
  WEBSOCKET_URL: getApiBaseUrl().replace(/^https?/, 'ws') + '/ws',
  
  // 请求超时时间（毫秒）
  TIMEOUT: 10000,
  
  // 请求重试次数
  RETRY_COUNT: 3,
  
  // 请求重试延迟（毫秒）
  RETRY_DELAY: 1000,
  
  // 请求头配置
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
} as const;

/**
 * API端点配置
 * 统一管理所有API端点路径
 */
export const API_ENDPOINTS = {
  // 认证相关
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    USERINFO: '/auth/userinfo',
    REFRESH: '/auth/refresh',
    CHANGE_PASSWORD: '/auth/change-password',
  },
  
  // 权限管理
  PERMISSION: {
    USER_MENUS: '/api/sys/permission/user-menus', // 获取用户菜单
    USER_ROLES: '/api/sys/permission/user-roles', // 获取用户角色
    USER_ROLE_IDS: '/api/sys/permission/user-role-ids', // 获取用户角色ID列表
    ROLE_MENUS: '/api/sys/permission/role-menus', // 获取角色菜单
    ASSIGN_USER_ROLES: '/api/sys/permission/assign-user-roles', // 分配用户角色
    ASSIGN_ROLE_MENUS: '/api/sys/permission/assign-role-menus', // 分配角色菜单
    CHECK_PERMISSION: '/api/sys/permission/check', // 检查权限
  },
  
  // 用户管理
  USER: {
    LIST: '/api/sys/user/list',
    DETAIL: '/api/sys/user',
    CREATE: '/api/sys/user',
    UPDATE: '/api/sys/user',
    DELETE: '/api/sys/user',
    RESET_PASSWORD: '/api/sys/user/reset-password',
  },
  
  // 角色管理
  ROLE: {
    LIST: '/api/sys/role/list',
    DETAIL: '/api/sys/role',
    CREATE: '/api/sys/role',
    UPDATE: '/api/sys/role',
    DELETE: '/api/sys/role',
  },
  
  // 菜单管理
  MENU: {
    LIST: '/api/sys/menu/list',
    DETAIL: '/api/sys/menu',
    CREATE: '/api/sys/menu',
    UPDATE: '/api/sys/menu',
    DELETE: '/api/sys/menu',
    TREE: '/api/sys/menu/tree', // 获取菜单树
  },
  
  // 字典管理
  DICT: {
    TYPE_LIST: '/api/sys/dict/type/list',
    TYPE_DETAIL: '/api/sys/dict/type',
    TYPE_CREATE: '/api/sys/dict/type',
    TYPE_UPDATE: '/api/sys/dict/type',
    TYPE_DELETE: '/api/sys/dict/type',
    TYPE_BATCH_DELETE: '/api/sys/dict/type/batch',
    DATA_LIST: '/api/sys/dict/data/list',
    DATA_DETAIL: '/api/sys/dict/data',
    DATA_CREATE: '/api/sys/dict/data',
    DATA_UPDATE: '/api/sys/dict/data',
    DATA_DELETE: '/api/sys/dict/data',
    DATA_BATCH_DELETE: '/api/sys/dict/data/batch',
    // 获取完整字典数据的接口
    DATA_ALL: '/api/sys/dict/data/all',
    DATA_ALL_ENABLED: '/api/sys/dict/data/all/enabled',
    DATA_ALL_BY_TYPE: '/api/sys/dict/data/all',
    DATA_BATCH: '/api/sys/dict/data/batch',
  },
  
  // 日志管理
  LOG: {
    LIST: '/api/sys/log/list',
    DETAIL: '/api/sys/log',
    CREATE: '/api/sys/log',
    STATISTICS: '/api/sys/log/statistics',
    EXPORT: '/api/sys/log/export',
    DELETE: '/api/sys/log',
    CLEAN: '/api/sys/log/clean',
    CONFIG: '/api/sys/log/config',
  },
  
  // 新闻相关
  NEWS: {
    LIST: '/api/biz/news/list',
    DETAIL: '/api/biz/news',
    CREATE: '/api/biz/news',
    UPDATE: '/api/biz/news',
    DELETE: '/api/biz/news',
    BATCH_DELETE: '/api/biz/news/batch',
    HOT: '/api/biz/news/hot',
  },
  
  // 机构相关
  INSTITUTION: {
    LIST: '/api/biz/institution/list',
    DETAIL: '/api/biz/institution',
    CREATE: '/api/biz/institution',
    UPDATE: '/api/biz/institution',
    DELETE: '/api/biz/institution',
    BATCH_DELETE: '/api/biz/institution/batch',
  },
  
  // 需求发布相关
  DEMAND: {
    LIST: '/api/biz/demand/list',
    DETAIL: '/api/biz/demand',
    CREATE: '/api/biz/demand',
    UPDATE: '/api/biz/demand',
    DELETE: '/api/biz/demand',
    BATCH_DELETE: '/api/biz/demand/batch',
    PUBLISH: '/api/biz/demand', // 发布需求
    OFFLINE: '/api/biz/demand', // 下架需求
  },
  
  // 展示相关
  SHOWCASE: {
    LIST: '/api/biz/showcase/list',
    DETAIL: '/api/biz/showcase',
    CREATE: '/api/biz/showcase',
    UPDATE: '/api/biz/showcase',
    DELETE: '/api/biz/showcase',
    BATCH_DELETE: '/api/biz/showcase/batch',
  },
  
  // 政策相关
  POLICY: {
    LIST: '/api/biz/policy/list',
    DETAIL: '/api/biz/policy',
    CREATE: '/api/biz/policy',
    UPDATE: '/api/biz/policy',
    DELETE: '/api/biz/policy',
    BATCH_DELETE: '/api/biz/policy/batch',
  },
  
  // 业务指南相关
  BUSINESS: {
    LIST: '/api/biz/business/list',
    DETAIL: '/api/biz/business',
    CREATE: '/api/biz/business',
    UPDATE: '/api/biz/business',
    DELETE: '/api/biz/business',
    BATCH_DELETE: '/api/biz/business/batch',
  },
  
  // 平台简介相关
  PLATFORM: {
    INFO: '/api/biz/platform/info',
    UPDATE: '/api/biz/platform/update',
    CREATE: '/api/biz/platform/create',
    // 统计数据配置相关API
    STATISTICS_CONFIG: '/api/biz/platform/statistics/config',
    STATISTICS_CONFIG_BY_CATEGORY: '/api/biz/platform/statistics/config/category',
    STATISTICS_CATEGORIES: '/api/biz/platform/statistics/categories',
  },
  
  // 通用配置
  COMMON_CONFIG: {
    LIST: '/api/sys/common-config/list',
    DETAIL: '/api/sys/common-config',
    CREATE: '/api/sys/common-config',
    UPDATE: '/api/sys/common-config',
    DELETE: '/api/sys/common-config',
    GET_BY_KEY: '/api/sys/common-config/key',
  },
  
  // 轮播图管理
  CAROUSEL: {
    LIST: '/api/biz/carousel/list',
    ENABLED: '/api/carousel/enabled',
    DETAIL: '/api/biz/carousel',
    CREATE: '/api/biz/carousel',
    UPDATE: '/api/biz/carousel',
    DELETE: '/api/biz/carousel',
  },
  
  // WebSocket相关
  WEBSOCKET: {
    ENDPOINT: '/ws',
    CHAT: '/app/websocket/chat',
    TOPIC: '/topic/messages',
  },
} as const;

/**
 * HTTP状态码常量
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

/**
 * 响应状态码常量
 */
export const RESPONSE_CODE = {
  SUCCESS: 200,
  ERROR: 500,
  VALIDATE_FAILED: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
} as const;

/**
 * 错误消息常量
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: '网络连接失败，请检查网络设置',
  TIMEOUT_ERROR: '请求超时，请稍后重试',
  UNAUTHORIZED: '未授权访问，请重新登录',
  FORBIDDEN: '权限不足，无法访问该资源',
  NOT_FOUND: '请求的资源不存在',
  SERVER_ERROR: '服务器内部错误，请稍后重试',
  UNKNOWN_ERROR: '未知错误，请稍后重试',
} as const;

